import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  SentimentToggleButton,
  type SentimentType,
} from "@repo/ui/Buttons/sentimentToggleButton";
import { PopoverArrow } from "@repo/ui/popover/arrow";
import { popoverOffsetForArrow, popoverStyle } from "@repo/ui/popover/style";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { type FC, useEffect, useState } from "react";
import {
  DialogTrigger,
  Popover,
  ToggleButtonGroup,
} from "react-aria-components";
import { useNavigate } from "react-router";
import { usePlaybookGradingVoiceAgentCallFeedbackMutation } from "../../api/entities/performancePlaybook.js";
import { appPaths } from "../../app/appPaths.js";
import { VoiceAgentCallFeedbackPopoverContent } from "./voiceAgentCallFeedbackPopoverContent.js";

interface VoiceAgentCallFeedbackWidgetProps {
  /** The grading this feedback is for. */
  gradingId?: GradingId;
  /** The agent ID to navigate to for editing (optional - only shown if provided) */
  teammateAgentId?: string;
  existingFeedback: {
    sentiment: "positive" | "negative";
    commentary: string;
  } | null;
}

export const VoiceAgentCallFeedbackWidget: FC<
  VoiceAgentCallFeedbackWidgetProps
> = (props) => {
  const { gradingId, teammateAgentId, existingFeedback } = props;
  const navigate = useNavigate();

  const [submitFeedback] = usePlaybookGradingVoiceAgentCallFeedbackMutation();

  // Track the committed sentiment (what's actually saved and displayed)
  const [committedSentimentType, setCommittedSentimentType] = useState<
    Set<SentimentType>
  >(() => {
    if (existingFeedback?.sentiment) {
      return new Set([existingFeedback.sentiment]);
    }
    return new Set();
  });

  // Update committed state when existingFeedback changes
  useEffect(() => {
    const newState = existingFeedback?.sentiment
      ? new Set([existingFeedback.sentiment])
      : new Set<SentimentType>();
    setCommittedSentimentType(newState);
  }, [existingFeedback]);

  const handleFeedbackSubmit = async (
    sentiment: SentimentType,
    commentary: string,
  ) => {
    if (!gradingId) {
      return;
    }

    try {
      await submitFeedback({
        gradingId,
        feedback: {
          sentiment,
          commentary,
        },
      }).unwrap();

      // Update committed state after successful submit
      setCommittedSentimentType(new Set([sentiment]));
    } catch (error) {
      console.error("Error submitting feedback:", error);
      showToast({
        title: "Failed to submit feedback",
        variant: ToastVariant.Error,
      });
      throw error; // Re-throw so the popover knows submission failed
    }
  };

  const handleEditAgent = (sentiment: SentimentType, commentary: string) => {
    if (!teammateAgentId || !gradingId) return;

    const prefillData = { gradingId, sentiment, commentary };
    const prefillParam = encodeURIComponent(JSON.stringify(prefillData));
    navigate(
      `${appPaths.teammateAgentConfiguration.build({ teammateAgentId })}?prefill=${prefillParam}`,
    );
  };

  // Only show "Edit agent" option if we have the necessary IDs
  const canEditAgent = Boolean(teammateAgentId && gradingId);

  return (
    <ToggleButtonGroup
      className="flex space-x-1"
      selectionMode="single"
      selectedKeys={committedSentimentType}
      onSelectionChange={() => {
        // Don't update selection on click - only highlight after successful submission
      }}
    >
      <DialogTrigger>
        <SentimentToggleButton
          sentimentType="positive"
          tooltipContent={
            existingFeedback?.sentiment === "positive"
              ? existingFeedback?.commentary
              : undefined
          }
        />
        <Popover
          className={popoverStyle().popover({
            className: "w-80",
          })}
          placement="bottom start"
          offset={popoverOffsetForArrow}
        >
          <PopoverArrow />
          <VoiceAgentCallFeedbackPopoverContent
            sentimentType="positive"
            onSubmit={(commentary) =>
              handleFeedbackSubmit("positive", commentary)
            }
            onEditAgent={canEditAgent ? handleEditAgent : undefined}
            existingFeedback={existingFeedback}
          />
        </Popover>
      </DialogTrigger>

      <DialogTrigger>
        <SentimentToggleButton
          sentimentType="negative"
          tooltipContent={
            existingFeedback?.sentiment === "negative"
              ? existingFeedback?.commentary
              : undefined
          }
        />
        <Popover
          className={popoverStyle().popover({
            className: "w-80",
          })}
          placement="bottom start"
          offset={popoverOffsetForArrow}
        >
          <PopoverArrow />
          <VoiceAgentCallFeedbackPopoverContent
            sentimentType="negative"
            onSubmit={(commentary) =>
              handleFeedbackSubmit("negative", commentary)
            }
            onEditAgent={canEditAgent ? handleEditAgent : undefined}
            existingFeedback={existingFeedback}
          />
        </Popover>
      </DialogTrigger>
    </ToggleButtonGroup>
  );
};
